import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Brain,
  FlaskConical,
  GraduationCap,
  LineChart,
  PencilLine,
  ShieldCheck,
  Sparkles,
  Target,
  Timer,
  TrendingUp,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

/**
 * `/` — LandingHub
 *
 * Hub público da plataforma DMZ Vestibulares. Apresenta os dois produtos
 * lado a lado (hero duplo), cada um com cor de accent própria, e duas
 * seções dedicadas abaixo. Conforme docs/ADR/ADR-001-multitenant.md.
 */

interface TenantHero {
  slug: "einstein" | "usp";
  shortName: string;
  course: string;
  banca: string;
  tagline: string;
  description: string;
  bullets: Array<{ label: string; value: string }>;
  cta: { to: string; label: string; variant: "gradient" | "primary" | "outline" };
  status: "active" | "waitlist";
  /** HSL string sem hsl() — usado em --color-accent local */
  accentHsl: string;
  accentDeepHsl: string;
  accentSoftHsl: string;
  decorTones: { blob: "violet" | "pink" | "sky"; star: "yellow" | "mint" | "coral" };
}

const TENANTS: TenantHero[] = [
  {
    slug: "einstein",
    shortName: "PsiEinstein PatternLab",
    course: "Psicologia · Faculdade Einstein",
    banca: "Banca Vunesp",
    tagline: "Domine o padrão Vunesp.",
    description:
      "100 pontos, 5 horas, fase única. Metade da seleção está fora da múltipla escolha — redação Vunesp e cinco discursivas valem 50 pontos. Aqui você treina exatamente esse padrão.",
    bullets: [
      { label: "Banca", value: "Vunesp" },
      { label: "Estrutura", value: "50 obj · 5 disc · redação" },
      { label: "Tempo", value: "5h · fase única" },
    ],
    cta: { to: "/einstein", label: "Entrar no PsiEinstein", variant: "gradient" },
    status: "active",
    accentHsl: "255 78% 62%",
    accentDeepHsl: "255 75% 45%",
    accentSoftHsl: "255 90% 96%",
    decorTones: { blob: "violet", star: "yellow" },
  },
  {
    slug: "usp",
    shortName: "FuvestPro USP",
    course: "Engenharia de Produção · Poli-USP",
    banca: "Banca Fuvest",
    tagline: "Engenharia reversa da Fuvest.",
    description:
      "Duas fases, 14 disciplinas, ano todo de preparação. Treinamento calibrado em 10 anos de prova Fuvest, com foco em Engenharia de Produção da Poli-USP.",
    bullets: [
      { label: "Banca", value: "Fuvest" },
      { label: "Estrutura", value: "1ª fase · 2ª fase · redação" },
      { label: "Tempo", value: "90Q + discursivas" },
    ],
    cta: { to: "/usp", label: "Entrar na lista de espera", variant: "outline" },
    status: "waitlist",
    accentHsl: "222 65% 50%",
    accentDeepHsl: "222 70% 35%",
    accentSoftHsl: "222 80% 95%",
    decorTones: { blob: "sky", star: "mint" },
  },
];

function TenantCard({ tenant }: { tenant: TenantHero }) {
  const tenantStyle = {
    "--color-accent": `hsl(${tenant.accentHsl})`,
    "--color-accent-deep": `hsl(${tenant.accentDeepHsl})`,
    "--color-accent-soft": `hsl(${tenant.accentSoftHsl})`,
  } as React.CSSProperties;

  return (
    <article
      style={tenantStyle}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-8 shadow-[var(--shadow-sm)] transition-shadow duration-200 hover:shadow-[var(--shadow-md)] md:p-10"
    >
      {/* Brand band */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-1.5 rounded-t-2xl"
        style={{ background: `linear-gradient(90deg, hsl(${tenant.accentHsl}), hsl(${tenant.accentDeepHsl}))` }}
      />

      <header className="flex items-start justify-between gap-4">
        <div>
          <Badge
            className="gap-1.5"
            style={{
              backgroundColor: `hsl(${tenant.accentHsl})`,
              color: "white",
              border: "none",
            }}
          >
            <Sparkles className="size-3" aria-hidden />
            {tenant.banca}
          </Badge>
          <h2 className="mt-5 font-display text-[length:var(--text-display-sm)] font-semibold leading-tight tracking-[var(--text-display-sm--letter-spacing)]">
            {tenant.shortName}
          </h2>
          <p className="mt-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            {tenant.course}
          </p>
        </div>
        {tenant.status === "waitlist" && (
          <Badge variant="outline" className="shrink-0 gap-1.5">
            <Timer className="size-3" aria-hidden />
            Em construção
          </Badge>
        )}
      </header>

      <p className="mt-6 text-[length:var(--text-body-lg)] font-medium text-[color:var(--color-foreground)] leading-snug">
        {tenant.tagline}
      </p>

      <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed text-pretty">
        {tenant.description}
      </p>

      <dl className="mt-7 flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:gap-3">
        {tenant.bullets.map((b) => (
          <div
            key={b.label}
            className="flex items-baseline justify-between gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 sm:block sm:px-3 sm:py-3"
          >
            <dt className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              {b.label}
            </dt>
            <dd
              data-tabular="true"
              className="text-right font-display text-[length:var(--text-body-sm)] font-semibold leading-tight sm:mt-1 sm:text-left"
            >
              {b.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button asChild size="lg" variant={tenant.cta.variant} className="flex-1">
          <Link to={tenant.cta.to}>
            {tenant.cta.label}
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>
    </article>
  );
}

const einsteinModules: Array<{ icon: typeof Target; title: string; description: string }> = [
  {
    icon: Target,
    title: "Diagnóstico calibrado",
    description: "Mini-prova com objetivas, redação curta e discursivas. Foto inicial com psicometria.",
  },
  {
    icon: LineChart,
    title: "Plano adaptativo",
    description: "Prioriza ganho marginal: peso na prova × lacuna individual × tempo restante.",
  },
  {
    icon: PencilLine,
    title: "Redação Vunesp",
    description: "Rubrica oficial. Detector de modelo pronto. Texto ou foto manuscrita.",
  },
  {
    icon: Timer,
    title: "Simulado 5h",
    description: "50 objetivas + 5 discursivas + redação no formato Einstein, com nota padronizada.",
  },
];

const uspModules: Array<{ icon: typeof Target; title: string; description: string }> = [
  {
    icon: Brain,
    title: "Engenharia reversa da Fuvest",
    description: "10 anos de prova mapeados em taxonomia de 9 níveis. Padrões cíclicos identificados.",
  },
  {
    icon: GraduationCap,
    title: "Foco Eng. Produção",
    description: "Pesos do curso, perfil intelectual valorizado pela Poli, repertório alinhado.",
  },
  {
    icon: FlaskConical,
    title: "Discursivas 2ª fase",
    description: "Correção item a item por disciplina, com resposta-modelo Fuvest comentada.",
  },
  {
    icon: TrendingUp,
    title: "Nota projetada",
    description: "Estimativa de nota da 1ª + 2ª fase com pesos do curso, atualizada por sessão.",
  },
];

function TenantSection({
  tenant,
  modules,
}: {
  tenant: TenantHero;
  modules: typeof einsteinModules;
}) {
  const sectionStyle = {
    "--color-accent": `hsl(${tenant.accentHsl})`,
    "--color-accent-deep": `hsl(${tenant.accentDeepHsl})`,
    "--color-accent-soft": `hsl(${tenant.accentSoftHsl})`,
  } as React.CSSProperties;

  return (
    <section
      style={sectionStyle}
      className="relative overflow-hidden border-t border-[--color-border] bg-[color:var(--color-background)] py-24 md:py-32"
    >
      <Blob
        color={tenant.decorTones.blob}
        className="pointer-events-none absolute -right-32 top-20 w-96 opacity-25 blur-2xl"
      />
      <SoftStar
        color={tenant.decorTones.star}
        className="pointer-events-none absolute left-10 bottom-16 w-20 opacity-50"
      />

      <Container size="wide" className="relative">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Badge
              className="w-fit gap-1.5"
              style={{
                backgroundColor: `hsl(${tenant.accentHsl})`,
                color: "white",
                border: "none",
              }}
            >
              <Sparkles className="size-3" aria-hidden />
              {tenant.shortName}
            </Badge>
            <h3 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              {tenant.tagline}
            </h3>
            <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-relaxed text-pretty max-w-xl">
              {tenant.description}
            </p>
          </div>
          <Button asChild size="lg" variant={tenant.status === "active" ? "primary" : "outline"}>
            <Link to={tenant.cta.to}>
              {tenant.status === "active" ? `Entrar no ${tenant.shortName}` : "Lista de espera"}
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </Button>
        </div>

        <ul className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {modules.map(({ icon: Icon, title, description }) => (
            <li
              key={title}
              className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)] lift"
            >
              <span
                className="chip size-10"
                style={{
                  backgroundColor: `hsl(${tenant.accentSoftHsl})`,
                  color: `hsl(${tenant.accentDeepHsl})`,
                }}
              >
                <Icon className="size-5" aria-hidden />
              </span>
              <h4 className="mt-4 font-display text-[length:var(--text-body)] font-semibold leading-tight">
                {title}
              </h4>
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                {description}
              </p>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

export function LandingHubPage() {
  return (
    <>
      {/* ───── HERO duplo ───── */}
      <section className="relative overflow-hidden bg-mesh">
        <Blob
          color="violet"
          className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob"
        />
        <Blob
          color="sky"
          className="pointer-events-none absolute -right-24 top-40 w-80 opacity-25 blur-2xl animate-blob"
        />
        <SoftStar
          color="yellow"
          className="pointer-events-none absolute right-10 bottom-20 w-24 opacity-50 animate-float"
        />

        <Container size="wide" className="relative pt-20 pb-24 md:pt-28 md:pb-28">
          <div className="max-w-3xl animate-fade-up">
            <Badge variant="accentSolid" className="gap-2">
              <GraduationCap className="size-3" aria-hidden />
              Neuronix Study · plataforma de alto desempenho
            </Badge>
            <h1 className="mt-7 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
              Dois caminhos. <span className="text-gradient-accent">Uma plataforma</span> de preparação séria.
            </h1>
            <p className="mt-6 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              Engenharia reversa de banca, taxonomia detalhada, simulados calibrados,
              correção de redação e discursivas com rubrica oficial. Escolha o vestibular
              que você vai dominar.
            </p>
          </div>

          <div className="mt-14 grid gap-6 animate-fade-up [animation-delay:120ms] lg:grid-cols-2">
            {TENANTS.map((t) => (
              <TenantCard key={t.slug} tenant={t} />
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="size-3.5" aria-hidden />
              LGPD · dados minimizados
            </span>
            <span className="inline-flex items-center gap-2">
              <Brain className="size-3.5" aria-hidden />
              Psicometria por banca
            </span>
            <span className="inline-flex items-center gap-2">
              <BookOpen className="size-3.5" aria-hidden />
              Fontes oficiais licenciadas
            </span>
          </div>
        </Container>
      </section>

      {/* ───── Seção dedicada · Einstein ───── */}
      <TenantSection tenant={TENANTS[0]!} modules={einsteinModules} />

      {/* ───── Seção dedicada · USP ───── */}
      <TenantSection tenant={TENANTS[1]!} modules={uspModules} />
    </>
  );
}
